<template>
  <el-config-provider :locale="elfr">
    <div class="app-shell">
      <aside class="app-shell__aside">
        <NuxtLink :prefetch="false" class="app-shell__aside__logo" to="/app">
          <SiteLogo class="app-shell__aside__logo__img" />
          <SiteLogoTitle class="app-shell__aside__logo__text" />
        </NuxtLink>

        <nav v-if="siteIdentity.identity.value" class="app-shell__aside__nav">
          <NuxtLink
            v-for="item in navItems"
            :key="item.to"
            :to="item.to"
            :prefetch="false"
            class="app-shell__aside__nav__link"
            :class="{ 'app-shell__aside__nav__link--active': isActive(item.to) }"
          >
            <el-icon class="app-shell__aside__nav__link__icon">
              <component :is="item.icon" />
            </el-icon>
            <span>{{ item.label }}</span>
          </NuxtLink>
        </nav>

        <el-popover
          v-if="siteIdentity.identity.value"
          placement="right-end"
          trigger="click"
          :width="240"
          popper-class="app-user-menu-popper"
        >
          <template #reference>
            <div class="app-shell__aside__user">
              <el-avatar
                shape="square"
                :size="32"
                src="https://cube.elemecdn.com/9/c2/f0ee8a3c7c9638a54940382568c9dpng.png"
              />
              <div class="app-shell__aside__user__info">
                <div class="app-shell__aside__user__name">{{ siteIdentity.identity.value.name }}</div>
                <div class="app-shell__aside__user__email">{{ siteIdentity.identity.value.email }}</div>
              </div>
            </div>
          </template>
          <div class="app-user-menu">
            <div class="app-user-menu__header">
              <div class="app-user-menu__name">{{ siteIdentity.identity.value.name }}</div>
              <div class="app-user-menu__email">{{ siteIdentity.identity.value.email }}</div>
            </div>
            <div class="app-user-menu__sep" />
            <div class="app-user-menu__item" @click="$router.push('/compte')">
              <el-icon><User /></el-icon>
              Mon compte
            </div>
            <div class="app-user-menu__sep" />
            <div class="app-user-menu__item app-user-menu__item--danger" @click="logout">
              <el-icon><SwitchButton /></el-icon>
              Me déconnecter
            </div>
          </div>
        </el-popover>
      </aside>

      <main class="app-shell__main">
        <slot />
      </main>
    </div>
  </el-config-provider>
</template>

<script setup>
  import elfr from 'element-plus/dist/locale/fr.mjs'
  import SiteLogo from '@/components/SiteLogo'
  import SiteLogoTitle from '@/components/SiteLogoTitle'
  import useIdentity from '@/components/SiteIdentity'
  import { Calendar, Document, User, SwitchButton } from '@element-plus/icons-vue'

  const siteIdentity = useIdentity()
  const config = useRuntimeConfig()
  const route = useRoute()

  const navItems = [
    { to: '/app', label: 'CRA', icon: Calendar },
    { to: '/contrats', label: 'Contrats', icon: Document },
  ]

  useHead({
    link: [{
      href: 'https://fonts.googleapis.com/css2?family=Outfit:wght@100;200;300;400;500;600;700&display=swap',
      rel: 'stylesheet'
    }]
  })

  const isActive = (to) => {
    if (to === '/app') return route.path === '/' || route.path === '/app'
    return route.path === to || route.path.startsWith(to + '/')
  }

  const logout = async () => {
    try {
      await $fetch(`${config.public.apiBaseUrl}/session?auth_token=${siteIdentity.authentity.value}`, {
        method: 'DELETE'
      })
    } catch (e) {}
    await siteIdentity.resetIdentity()
    useRouter().push('/')
  }
</script>

<style lang="scss">
  html, body {
    margin: 0;
    padding: 0;
    font-family: 'Outfit', sans-serif;
  }

  .app-shell {
    display: flex;
    min-height: 100vh;
    background: #f7f9fb;

    &__aside {
      width: 240px;
      flex-shrink: 0;
      background: #fff;
      border-right: 1px solid #e6e9ee;
      display: flex;
      flex-direction: column;
      padding: 20px 12px;
      gap: 24px;
      position: sticky;
      top: 0;
      height: 100vh;
      box-sizing: border-box;

      &__logo {
        display: flex;
        align-items: center;
        gap: 10px;
        text-decoration: none;
        padding: 0 8px;

        .site-logo-title__main {
          font-size: 18.5px;
          line-height: 21px;
        }
        .site-logo-title__sub {
          font-size: 6.75px;
          line-height: 9px;
          letter-spacing: 2px;
        }
      }

      &__nav {
        display: flex;
        flex-direction: column;
        gap: 4px;
        flex-grow: 1;

        &__link {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          border-radius: 8px;
          color: #5a6776;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: background 0.15s, color 0.15s;

          &__icon {
            font-size: 18px;
          }

          &:hover {
            background: #f1f3f6;
            color: #01498B;
          }

          &--active {
            color: #01498B;
            background: rgba(1, 73, 139, 0.1);
            font-weight: 600;
          }
        }
      }

      &__user {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 8px;
        border-radius: 8px;
        cursor: pointer;
        transition: background 0.15s;

        &:hover {
          background: #f1f3f6;
        }

        &__info {
          flex-grow: 1;
          min-width: 0;
        }

        &__name {
          font-size: 13px;
          font-weight: 600;
          color: #2c3e50;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        &__email {
          font-size: 11px;
          color: #6b7785;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }

    &__main {
      flex-grow: 1;
      min-width: 0;
      padding: 24px 32px;
    }
  }

  .app-user-menu-popper {
    padding: 0 !important;
    border-radius: 12px !important;
    border: 1px solid #e6e9ee !important;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08) !important;
    overflow: hidden;
  }

  .app-user-menu {
    display: flex;
    flex-direction: column;
    font-family: 'Outfit', sans-serif;

    &__header {
      padding: 14px 16px;
      background: #f7f9fb;
    }

    &__name {
      font-size: 14px;
      font-weight: 600;
      color: #2c3e50;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__email {
      font-size: 12px;
      color: #6b7785;
      margin-top: 2px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__sep {
      height: 1px;
      background: #eef0f3;
    }

    &__item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 16px;
      font-size: 14px;
      color: #3a4a5c;
      cursor: pointer;
      transition: background 0.15s, color 0.15s;

      .el-icon {
        font-size: 16px;
        color: #6b7785;
      }

      &:hover {
        background: #f1f3f6;
        color: #01498B;

        .el-icon {
          color: #01498B;
        }
      }

      &--danger {
        color: #e74c3c;

        .el-icon {
          color: #e74c3c;
        }

        &:hover {
          background: #fef0f0;
          color: #c0392b;

          .el-icon {
            color: #c0392b;
          }
        }
      }
    }
  }

  @media screen and (max-width: 1024px) {
    .app-shell {
      flex-direction: column;

      &__aside {
        width: 100%;
        height: auto;
        position: static;
        border-right: none;
        border-bottom: 1px solid #e6e9ee;
        flex-direction: row;
        align-items: center;
        padding: 12px 16px;
        gap: 12px;

        &__logo .site-logo-title { display: none; }

        &__nav {
          flex-direction: row;
          flex-grow: 0;

          &__link span { display: none; }
        }
      }

      &__main {
        padding: 16px;
      }
    }
  }
</style>
